import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {isSchemaFieldDatabaseBacked} from 'folder-items/FileConstants';
import {PrimaryExperimentalRoleType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {InferAnnotationParamsResponse} from 'samples/plate-maps/annotate-plate-modal/ai-assistant/AnnotationAssistantDAO';
import NumberFieldAssignmentSettings from 'samples/plate-maps/annotate-plate-modal/NumberFieldAssignmentSettings';
import SelectorFieldAssignmentSettings from 'samples/plate-maps/annotate-plate-modal/SelectorFieldAssignmentSettings';
import TestDilutionFactorLayerDisplay from 'samples/plate-maps/annotate-plate-modal/TestDilutionFactorLayerDisplay';
import TextFieldAssignmentSettings from 'samples/plate-maps/annotate-plate-modal/TextFieldAssignmentSettings';
import WellRoleAssignmentSettings from 'samples/plate-maps/annotate-plate-modal/WellRoleAssignmentSettings';
import {
  AssignmentGroup,
  AssignmentGroupValue,
  AssignmentGroupValueTypeByLayer,
  BaseSchematizedAttribute,
  PlateMap,
  PlateMapDisplayLayerAttribute,
  PlateMapWellDisplayLayer,
  SchematizedAttribute,
} from 'samples/plate-maps/PlateMapConstants';
import {getDisplayLayerAttributeKey} from 'samples/plate-maps/PlateMapUtil';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';

import {ProvidedConcentrationLayerSettings} from './ConcentrationLayerSettings';

export interface AnnotatePlateSettingsProps {
  plateMap: PlateMap;
  onEditPlateMap: (
    plateMap: PlateMap,
    additionalMetadata: {
      primaryRole?: PrimaryExperimentalRoleType;
      subrole?: string | null;
      annotatedByAi?: boolean;
      layerUsesDilution?: boolean;
    }
  ) => void;
  selectedWellIndices: Set<number>;
  deselectWells: () => void;
  previewPlateMap: PlateMap | null;
  setPlateMapWithPreview: (plateMap: PlateMap | null) => void;
  displayLayer: PlateMapWellDisplayLayer;
  selectedAttribute: PlateMapDisplayLayerAttribute;
  assignmentGroupsById: {[id: string]: AssignmentGroup<AssignmentGroupValue>};
  setAssignmentGroupsById: (groupsById: {[id: string]: AssignmentGroup<AssignmentGroupValue>}) => void;
  setIsAIPreview: (isAiPreview: boolean) => void;
  onSubmitAIPrompt: (userPrompt: string) => Promise<InferAnnotationParamsResponse>;
  onApplyAISuggestion: () => void;
  onDiscardAISuggestion: () => void;
  aiSuggestedPlateMap: PlateMap | null;
  setAISuggestedPlateMap: (plateMap: PlateMap | null) => void;
}

/**
 * Typeguard to differentiate between sentinel-type attributes and schematized attributes.
 */
function isSchematizedAttribute(attribute: PlateMapDisplayLayerAttribute): attribute is SchematizedAttribute {
  return attribute.hasOwnProperty('schemaField');
}

/**
 * Typeguard to check if a schematized attribute has a live SchemaField (as opposed to a MinimalSchemaField).
 */
function isLiveSchematizedAttribute(
  attribute: PlateMapDisplayLayerAttribute
): attribute is BaseSchematizedAttribute<SchemaField> {
  return isSchematizedAttribute(attribute) && isSchemaFieldDatabaseBacked(attribute.schemaField);
}

/**
 * Right side panel of annotate plate modal with settings to assign well roles and container schema attributes.
 */
export default function AnnotatePlateSettings({
  plateMap,
  onEditPlateMap,
  selectedWellIndices,
  deselectWells,
  previewPlateMap,
  setPlateMapWithPreview,
  displayLayer,
  selectedAttribute,
  assignmentGroupsById,
  setAssignmentGroupsById,
  setIsAIPreview,
  onSubmitAIPrompt,
  onApplyAISuggestion,
  onDiscardAISuggestion,
  aiSuggestedPlateMap,
  setAISuggestedPlateMap,
}: AnnotatePlateSettingsProps): JSX.Element {
  function onEditPlateMapWithoutTrackingParams(plateMap: PlateMap): void {
    onEditPlateMap(plateMap, {});
  }

  function onEditPlateMapForConcentrationLayer(plateMap: PlateMap, layerUsesDilution: boolean): void {
    onEditPlateMap(plateMap, {layerUsesDilution});
  }

  function getSchematizedLayerSettingsProps<
    TPlateMapDisplayLayer extends keyof AssignmentGroupValueTypeByLayer,
  >(): {
    key: string;
    plateMap: PlateMap;
    onEditPlateMap: (plateMap: PlateMap) => void;
    selectedWellIndices: Set<number>;
    deselectWells: () => void;
    assignmentGroupsById: {
      [id: string]: AssignmentGroup<AssignmentGroupValueTypeByLayer[TPlateMapDisplayLayer]>;
    };
    setAssignmentGroupsById: (groupsById: {
      [id: string]: AssignmentGroup<AssignmentGroupValueTypeByLayer[TPlateMapDisplayLayer]>;
    }) => void;
    selectedAttribute: BaseSchematizedAttribute<SchemaField>;
  } | null {
    assert(
      isSchematizedAttribute(selectedAttribute),
      `Expected schema ${displayLayer} to be a schematized attribute`
    );
    if (!isLiveSchematizedAttribute(selectedAttribute)) {
      // We might be rendering a stale schematized field using a MinimalSchemaField. In that case it won't be editable.
      return null;
    }
    return {
      key: getDisplayLayerAttributeKey(selectedAttribute),
      plateMap,
      onEditPlateMap: onEditPlateMapWithoutTrackingParams,
      selectedWellIndices,
      deselectWells,
      setAssignmentGroupsById: setAssignmentGroupsById as (groupsById: {
        [id: string]: AssignmentGroup<AssignmentGroupValueTypeByLayer[TPlateMapDisplayLayer]>;
      }) => void,
      assignmentGroupsById: assignmentGroupsById as {
        [id: string]: AssignmentGroup<AssignmentGroupValueTypeByLayer[TPlateMapDisplayLayer]>;
      },
      selectedAttribute,
    };
  }

  function renderSettings(): JSX.Element | null {
    switch (displayLayer) {
      case PlateMapWellDisplayLayer.WELL_ROLE: {
        return (
          <WellRoleAssignmentSettings
            plateMap={plateMap}
            onEditPlateMap={onEditPlateMap}
            selectedWellIndices={selectedWellIndices}
            deselectWells={deselectWells}
            setPlateMapWithPreview={setPlateMapWithPreview}
            setIsAIPreview={setIsAIPreview}
            onSubmitAIPrompt={onSubmitAIPrompt}
            onApplyAISuggestion={onApplyAISuggestion}
            onDiscardAISuggestion={onDiscardAISuggestion}
            aiSuggestedPlateMap={aiSuggestedPlateMap}
            setAISuggestedPlateMap={setAISuggestedPlateMap}
          />
        );
      }
      case PlateMapWellDisplayLayer.TEST_DILUTION_FACTOR: {
        return <TestDilutionFactorLayerDisplay plateMap={plateMap} />;
      }
      case PlateMapWellDisplayLayer.SCHEMA_FIELD_SELECTOR: {
        const settingsProps =
          getSchematizedLayerSettingsProps<PlateMapWellDisplayLayer.SCHEMA_FIELD_SELECTOR>();
        if (settingsProps == null) {
          return null;
        }
        return <SelectorFieldAssignmentSettings {...settingsProps} />;
      }
      case PlateMapWellDisplayLayer.SCHEMA_FIELD_TEXT: {
        const settingsProps = getSchematizedLayerSettingsProps<PlateMapWellDisplayLayer.SCHEMA_FIELD_TEXT>();
        if (settingsProps == null) {
          return null;
        }
        return <TextFieldAssignmentSettings {...settingsProps} />;
      }
      case PlateMapWellDisplayLayer.SCHEMA_FIELD_NUMBER: {
        const settingsProps =
          getSchematizedLayerSettingsProps<PlateMapWellDisplayLayer.SCHEMA_FIELD_NUMBER>();
        if (settingsProps == null) {
          return null;
        }
        return <NumberFieldAssignmentSettings {...settingsProps} />;
      }
      case PlateMapWellDisplayLayer.CONCENTRATION: {
        return (
          <ProvidedConcentrationLayerSettings
            plateMap={plateMap}
            onEditPlateMap={onEditPlateMapForConcentrationLayer}
            selectedWellIndices={selectedWellIndices}
            deselectWells={deselectWells}
            previewPlateMap={previewPlateMap}
            setPlateMapWithPreview={setPlateMapWithPreview}
          />
        );
      }
      default:
        return null;
    }
  }

  return <div className={css(styles.assignmentSection)}>{renderSettings()}</div>;
}

const styles = StyleSheet.create({
  assignmentSection: {
    width: '420px',
    maxWidth: '420px',
    flex: '0 0 420px',
    maxHeight: 'calc(100vh - 170px)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },
  layerDropdown: {
    marginBottom: 16,
  },
});
